import { AnimatedCircularProgress } from 'react-native-circular-progress';
import React, { useEffect } from 'react';

import { Typography } from '@components/Typography';
import { crashlyticsUtil } from '@lib/crashlytics';
import { useCountdown } from '@hooks/useCountdown';
import { useTheme } from '@providers/Theme';

type ProgressCircleProps = {
  targetTime: number;
  startTime: number;
};

function isValidNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export const ProgressCircle: React.FC<ProgressCircleProps> = ({
  targetTime,
  startTime,
}) => {
  const theme = useTheme();

  const isSmallScreen = theme.screen.width < theme.breakpoints.lg;
  const size = isSmallScreen ? 64 : 128;
  const width = isSmallScreen ? 6 : 16;

  const dateTimeAfterExpected = startTime + targetTime;
  const [hours, minutes, seconds] = useCountdown(dateTimeAfterExpected);

  const percentage =
    targetTime > 0
      ? Math.max(
          0,
          Math.min(
            100,
            100 -
              Math.round(((Date.now() - startTime) / targetTime) * 100)
          )
        )
      : 0;

  // Log once when the component mounts
  useEffect(() => {
    crashlyticsUtil.log(
      `ProgressCircle mounted (size=${size}, width=${width}, fill=${percentage})`
    );

    crashlyticsUtil.setAttributes({
      progress_target_time: String(targetTime),
      progress_start_time: String(startTime),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only log if something looks abnormal
  useEffect(() => {
    if (
      !isValidNumber(startTime) ||
      !isValidNumber(targetTime) ||
      !isValidNumber(percentage) ||
      percentage < 0 ||
      percentage > 100
    ) {
      crashlyticsUtil.log(
        `ProgressCircle invalid values: start=${startTime}, target=${targetTime}, fill=${percentage}, size=${size}, width=${width}`
      );
    }
  }, [startTime, targetTime, percentage, size, width]);

  return (
    <AnimatedCircularProgress
      size={size}
      width={width}
      fill={percentage}
      tintColor={
        percentage < 50
          ? theme.palette.primary[600]
          : theme.palette.success[400]
      }
      backgroundColor={
        percentage > 0
          ? theme.palette.grey[300]
          : theme.palette.primary[600]
      }
      rotation={0}
      lineCap="round"
    >
      {() => (
        <Typography
          weight="medium"
          size={{ xs: 'sm', lg: '2xl' }}
          variant="cool"
        >
          {hours !== 0
            ? `${hours}:${minutes}:${seconds}`
            : minutes !== 0
              ? `${minutes}:${seconds}`
              : '00:00'}
        </Typography>
      )}
    </AnimatedCircularProgress>
  );
};